import * as React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
/**
 * SelectChips (multi select with chips)
 *
 * Props:
 * - list:               array of option objects
 * - Label:              string (placeholder/label)
 * - required:           boolean
 * - disable:            boolean (disable input)
 * - onChange:           function (payload) -> void
 * - value:              controlled value (array of IDs or array of option objects)
 * - defaultValue:       default selected (array of option objects)
 * - keyField:           string, e.g. "ID_Client"
 * - valueField:         string, e.g. "Nom"
 * - dueShowValue:       boolean (show combined labels)
 * - DueValue:           boolean (append DeméValue to label)
 * - DeméValue:          string (field name to append for label, e.g. 'Code')
 * - widthSpecific:      number | string (custom width for lg/xl)
 * - ToValue:            boolean (if true, return array of objects with fields; else array of IDs)
 * - departField:        string (for "X -> Y" display)
 * - destinationField:   string (for "X -> Y" display)
 * - sx:                 object (extra sx)
 */
export default function SelectChips({
  list = [],
  Label,
  required = false,
  disable,
  onChange,
  value,                 // array of IDs or array of option objects
  defaultValue = [],     // array of option objects
  keyField,
  valueField,
  dueShowValue = false,
  DueValue = false,
  DeméValue,             // e.g., 'Code' (optional)
  widthSpecific,
  ToValue = false,
  departField,
  destinationField,
  sx = {},
}) {
  // ----- Helpers
  const getLabel = (option) => {
    if (!option) return '';
    if (dueShowValue) {
      if (departField && destinationField) {
        return `${option[departField] ?? ''} -> ${option[destinationField] ?? ''}`.trim();
      }
      if (DueValue) {
        const extra = DeméValue ? (option[DeméValue] ?? '') : '';
        return `${option[valueField] ?? ''} ${extra}`.trim();
      }
      return `${option[keyField]} - ${option[valueField] ?? ''}`.trim();
    }
    return option[valueField] ?? '';
  };

  const byKey = (id) => list.find((o) => o?.[keyField] === id);

  // Coerce controlled value into an array of option objects
  const selectedOptions = React.useMemo(() => {
    if (!value) return (Array.isArray(defaultValue) ? defaultValue : []);
    if (!Array.isArray(value)) return [];

    // If value array looks like IDs (primitive/strings/numbers), map to options
    const looksLikeIds = value.every((v) => typeof v !== 'object' || v == null || v[keyField] == null);
    if (looksLikeIds) {
      return value.map(byKey).filter(Boolean);
    }
    // Else it's already array of objects; normalize by keyField presence
    return value
      .map((v) => (v && v[keyField] != null ? v : byKey(v?.[keyField])))
      .filter(Boolean);
  }, [value, defaultValue, list, keyField]);

  // ----- Change handler
  const handleChange = (_, optionsArray) => {
    if (!onChange) return;

    if (ToValue) {
      // Return array of objects with useful fields
      const payload = optionsArray.map((opt) => {
        const base = {
          [keyField]: opt[keyField],
          [valueField]: opt[valueField],
        };
        if (departField && destinationField) {
          base.depart = opt[departField];
          base.destination = opt[destinationField];
        }
        if (DueValue && DeméValue) {
          base[DeméValue] = opt[DeméValue];
        }
        return base;
      });
      onChange(payload);
    } else {
      // Return array of IDs only
      onChange(optionsArray.map((opt) => opt[keyField]));
    }
  };

  return (
   <Autocomplete
  multiple
  disableCloseOnSelect
  disabled={disable}
  readOnly={disable}
  options={list}
  value={selectedOptions}
  onChange={handleChange}
  isOptionEqualToValue={(option, val) =>
    option?.[keyField] === val?.[keyField]
  }
  getOptionLabel={getLabel}
  filterSelectedOptions
  size="medium"
  limitTags={3}

  /* 🔥 VERY IMPORTANT FIX */
  disablePortal
  slotProps={{
    popper: {
      placement: "bottom-start",
      modifiers: [
        {
          name: "flip",
          enabled: false,
        },
        {
          name: "preventOverflow",
          enabled: true,
        },
        {
          name: "computeStyles",
          options: {
            adaptive: false, // 🔥 prevents shifting
          },
        },
      ],
    },
    paper: {
      sx: {
        p: 1,
        
        width: "100%",
        height: "200px"
      },
    },
  }}

  renderTags={(selected, getTagProps) =>
    selected.map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={option[keyField]}
        label={getLabel(option)}
        variant="outlined"
        sx={{ borderRadius: "12px" }}
      />
    ))
  }

  sx={{
    width: {
      xs: "100%",
      sm: "100%",
      md: "350px",
      lg: widthSpecific !== undefined ? widthSpecific : "250px",
      xl: widthSpecific !== undefined ? widthSpecific : "300px",
    },
    ...sx,
  }}

  renderInput={(params) => (
    <TextField
      {...params}
      placeholder={Label}
      label={
        <>
          {Label}
          {required && (
            <Typography
              component="span"
              sx={{ ml: "0.2rem", color: "red", fontSize: "14px" }}
            >
              *
            </Typography>
          )}
        </>
      }
    />
  )}
/>
  );
}
